import { useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { LightProbeGrid } from '../lib/lightProbeGrid'
import { lightProbeGridUrl } from '../lib/lightProbeGridKey'
import { fetchWithRetry } from '../lib/fetchRetry'
import { useLiveTune } from '../hooks/useLiveTune'

/**
 * Loads the baked SH grid for the current splat and lights the avatar with a
 * single THREE.LightProbe whose coefficients are trilinearly interpolated from
 * the grid by the avatar's world position every frame. Diffuse only (Phase A,
 * ENG-561).
 *
 * Off the React hot path: grid + probe + scratch buffers live in refs; useFrame
 * mutates the probe's SH coefficients in place (no per-frame allocation, no
 * React state). Renders nothing.
 *
 * No silent fallback (CLAUDE.md UX rule): a missing grid surfaces as
 * window.__LIGHT_PROBE_GRID__.state === 'no-cache' with a console pointer to
 * the bake command — never fake lighting.
 */
export default function LightProbeVolume({ splatUrl, splatVersion, reloadKey = 0 }) {
  const scene = useThree(s => s.scene)
  const tune = useLiveTune()
  const { lightProbeEnabled, lightProbeIntensity } = tune.lighting
  const gridRef = useRef(null)
  const probeRef = useRef(null)
  const shBuf = useRef(new Float32Array(27))
  const tmpVec = useRef(new THREE.Vector3())
  // Effective THREE.LightProbe.intensity = enabled ? multiplier : 0. Held in a
  // ref so the async grid-load closure can read the current value when it
  // installs the probe (the probe is created after this component mounts). The
  // effect below applies live edits to an already-installed probe; between the
  // two, no per-frame work is needed. Enable flag zeroes the contribution
  // without unmounting the probe.
  const intensityRef = useRef(lightProbeEnabled ? lightProbeIntensity : 0)
  useEffect(() => {
    intensityRef.current = lightProbeEnabled ? lightProbeIntensity : 0
    if (probeRef.current) probeRef.current.intensity = intensityRef.current
  }, [lightProbeEnabled, lightProbeIntensity])

  useEffect(() => {
    let cancelled = false
    const setState = (state, extra) => {
      if (typeof window !== 'undefined') window.__LIGHT_PROBE_GRID__ = { state, ...extra }
    }
    if (!splatUrl) { setState('idle'); return }
    setState('pending')

    ;(async () => {
      // ?v=<splatVersion> busts the proxy/browser cache after a (re)bake so the
      // freshly-baked grid is fetched, not a stale 404/old copy.
      const bust = splatVersion ? `?v=${encodeURIComponent(splatVersion)}` : ''
      const served = await lightProbeGridUrl(splatUrl)
      if (cancelled) return
      // null = the server says this environment has no baked grid. That is an
      // answer, so report it without a request; only a URL we were GIVEN gets
      // fetched (ENG-999 — the old code computed one and read absence off a 404,
      // which could not tell "unbaked" from "wrong path").
      if (!served) {
        console.warn(`[LightProbeVolume] no baked grid for ${splatUrl} (server lists none). Bake with: npm run bake-lightprobes -- ${splatUrl}`)
        setState('no-cache', { url: null })
        return
      }
      const url = served + bust
      // GET, not HEAD: the asset-manager env-file proxy is a GET-only route, so
      // HEAD 404s and would false-report no-cache. A missing grid is a real 404;
      // a SPA-fallback (bundled mode) is text/html and fails JSON parse below.
      const res = await fetchWithRetry(url, { cache: 'no-cache' }) // retry transient 5xx (AAM blip), not 404; revalidate via ETag so a server-side re-bake shows on plain reload
      if (cancelled) return
      if (!res.ok) {
        console.warn(`[LightProbeVolume] no baked grid at ${url}. Bake with: npm run bake-lightprobes -- ${splatUrl}`)
        setState('no-cache', { url })
        return
      }
      // Read text first so a parse failure can report WHAT the body was: an
      // HTML head means SPA-fallback / proxy error page (a routing problem);
      // a JSON head with a short length means the body was TRUNCATED in
      // flight (connection dropped mid-stream under load) — two different
      // bugs that a bare "non-JSON body" can't separate. Loud failure should
      // carry the evidence.
      const text = await res.text()
      let data
      try { data = JSON.parse(text) } catch {
        console.warn(`[LightProbeVolume] no baked grid at ${url} (200 but unparseable body: len=${text.length} head=${JSON.stringify(text.slice(0, 60))}). Bake with: npm run bake-lightprobes -- ${splatUrl}`)
        setState('no-cache', { url, bodyLen: text.length, bodyHead: text.slice(0, 60) })
        return
      }
      if (cancelled) return
      gridRef.current = new LightProbeGrid(data)
      const probe = new THREE.LightProbe()
      probe.intensity = intensityRef.current // honour the live gate/multiplier on install
      scene.add(probe)
      probeRef.current = probe
      if (typeof window !== 'undefined') window.__LIGHT_PROBE__ = probe
      setState('applied', { url, probeCount: data.probes.length })
      console.log(`[LightProbeVolume] applied grid from ${url} (${data.probes.length} probes)`)
    })().catch(err => {
      if (cancelled) return
      console.error('[LightProbeVolume] failed:', err)
      setState('error', { error: String(err) })
    })

    return () => {
      cancelled = true
      if (probeRef.current) { scene.remove(probeRef.current); probeRef.current = null }
      gridRef.current = null
      if (typeof window !== 'undefined') { delete window.__LIGHT_PROBE_GRID__; delete window.__LIGHT_PROBE__ }
    }
    // reloadKey bumps when a bake for this env completes (bakeReload.js) → re-run
    // so the freshly-baked grid loads without a manual env re-select (was the
    // "lighting ready but balls black" bug — the volume never refetched after
    // no-cache). splatVersion still re-runs it on a splat re-upload.
  }, [splatUrl, splatVersion, scene, reloadKey])

  useFrame(() => {
    const grid = gridRef.current, probe = probeRef.current
    if (!grid || !probe) return
    // __AVATAR_PROBE_POS__ overrides the sample point (tests + dev oracle);
    // otherwise sample at the avatar's world position, raised to body height.
    const override = (typeof window !== 'undefined' && window.__AVATAR_PROBE_POS__) || null
    let x, y, z
    if (override) {
      x = override[0]; y = override[1]; z = override[2]
    } else {
      // __AVATAR_SCENE__ is the GLB AvatarModel's combined group, and ONLY that
      // path publishes it. Splat (OGSModel) and stream (StreamAvatarModel)
      // characters never do — their SplatMesh mounts at the world origin — so
      // bailing here left probe.sh at its initial ZERO forever and every splat
      // character rendered unlit (black hair) even with a grid `applied`.
      // Treat "no GLB root" as an avatar at the origin instead of returning,
      // keeping the same body-height raise: the grid's lowest layer sits above
      // the floor, so an un-raised origin can fall outside it and interpolate
      // to zero — the raise is what lands the sample inside the volume.
      const root = (typeof window !== 'undefined' && window.__AVATAR_SCENE__) || null
      if (root) root.getWorldPosition(tmpVec.current)
      else tmpVec.current.set(0, 0, 0)
      x = tmpVec.current.x; y = tmpVec.current.y + 1.2; z = tmpVec.current.z
    }
    const sh = grid.sampleSHInto(shBuf.current, x, y, z)
    const coeffs = probe.sh.coefficients
    for (let i = 0; i < 9; i++) coeffs[i].set(sh[i * 3], sh[i * 3 + 1], sh[i * 3 + 2])
  })

  return null
}
